import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, UserPlus, X } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StandingRecipientResponse } from "@shared-core/data/api-types";
import type { AudienceSummary } from "@shared-core/data/marketing-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { TiersHelpNote } from "../app/components/domain/marketing/TiersHelpNote";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";

function StandingRecipientsPanel() {
  const dataAdapter = useDataAdapter();
  const [recipients, setRecipients] = useState<StandingRecipientResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setRecipients(await dataAdapter.listStandingRecipients());
    } catch (e) {
      console.error("marketing.standing_recipients.list.failed", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter]);

  function openAddDialog() {
    setNewEmail("");
    setNewNote("");
    setError(null);
    setAddOpen(true);
  }

  async function handleAdd() {
    if (!newEmail.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.addStandingRecipient(
        newEmail.trim(),
        newNote.trim() || undefined,
      );
      setAddOpen(false);
      await load();
    } catch (e) {
      console.error("marketing.standing_recipients.add.failed", e);
      setError("Couldn't add this recipient. Check the email and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(recipientId: string) {
    setRemovingId(recipientId);
    setRemoveError(null);
    try {
      await dataAdapter.removeStandingRecipient(recipientId);
      await load();
    } catch (e) {
      console.error("marketing.standing_recipients.remove.failed", e);
      setRemoveError("Couldn't remove this recipient. Please try again.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Standing recipients</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Always included in every audience, on top of its own rules — e.g. a
            reviewer who must see every send.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={openAddDialog}>
          <UserPlus className="size-4" />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        {removeError && (
          <p className="text-sm text-destructive mb-2">{removeError}</p>
        )}
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && recipients.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No standing recipients — every audience is scoped purely by its own
            rules.
          </p>
        )}
        {!loading && recipients.length > 0 && (
          <ul className="space-y-2">
            {recipients.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <div className="min-w-0">
                  <span className="text-foreground">{r.email}</span>
                  {r.note && (
                    <span className="text-muted-foreground"> — {r.note}</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  disabled={removingId === r.id}
                  onClick={() => handleRemove(r.id)}
                  aria-label={`Remove ${r.email}`}
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add standing recipient</DialogTitle>
            <DialogDescription>
              They're pinned into every existing audience immediately, and every
              audience created from now on.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="standing-recipient-email">Email</Label>
              <Input
                id="standing-recipient-email"
                type="email"
                placeholder="e.g. reviewer@agency.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="standing-recipient-note">Note (optional)</Label>
              <Input
                id="standing-recipient-note"
                placeholder="Why they're always included"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving || !newEmail.trim()}>
              {saving ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function AudiencesPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<AudienceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const res = await dataAdapter.listMarketingAudiences({ pageSize: 100 });
        if (!cancelled) setAudiences(res.items);
      } catch (e) {
        console.error("marketing.audiences.list.failed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  function openNewDialog() {
    setNewName("");
    setNewDescription("");
    setNewOpen(true);
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const audience = await dataAdapter.createMarketingAudience({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setNewOpen(false);
      navigate(paths.marketingAudienceBuilder({ audienceId: audience.id }));
    } catch (e) {
      console.error("marketing.audiences.create.failed", e);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-6">
      <MarketingNav active="audiences" />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl mb-1">Audiences</h1>
          <p className="text-sm text-muted-foreground">
            Reusable, named segments of people — built by rules and manual
            curation, targeted by many campaigns.
          </p>
          <div className="mt-2">
            <TiersHelpNote />
          </div>
        </div>
        <Button onClick={openNewDialog}>
          <Plus className="size-4" />
          New audience
        </Button>
      </div>

      <StandingRecipientsPanel />

      {loading && (
        <div className="text-sm text-muted-foreground">Loading audiences…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load audiences. Please try again.
        </div>
      )}

      {!loading && !error && audiences.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">No audiences yet</p>
          <p className="text-muted-foreground">
            Create an audience to build a reusable, consented pool to target.
          </p>
        </div>
      )}

      {!loading && !error && audiences.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {audiences.map((a) => (
            <Card
              key={a.id}
              className="cursor-pointer hover:bg-surface-hover transition-colors"
              onClick={() =>
                navigate(paths.marketingAudienceBuilder({ audienceId: a.id }))
              }
            >
              <CardHeader>
                <CardTitle className="text-base">{a.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{a.description}</p>
                <div className="flex flex-wrap gap-2 text-sm">
                  {(a.member_count ?? 0) > 0 && (
                    <span className="bg-primary/10 text-primary rounded-md px-2 py-0.5">
                      {a.member_count} members
                    </span>
                  )}
                  {(a.suppressed_count ?? 0) > 0 && (
                    <span className="bg-red-100 text-red-800 rounded-md px-2 py-0.5">
                      {a.suppressed_count} suppressed
                    </span>
                  )}
                  {a.campaign_count != null && (
                    <span className="bg-surface border border-border rounded-md px-2 py-0.5 text-muted-foreground">
                      {a.campaign_count} campaign
                      {a.campaign_count === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Updated {formatDateOrDash(a.updated_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New audience</DialogTitle>
            <DialogDescription>
              Give it a name and description. You'll add membership rules next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="audience-name">Audience name</Label>
              <Input
                id="audience-name"
                placeholder="e.g. Lapsed folder customers"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="audience-desc">Description</Label>
              <Input
                id="audience-desc"
                placeholder="Who is in this audience and why"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
            >
              {creating ? "Creating…" : "Create audience"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
